// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { SHOPPING_SECTIONS, type ShoppingListItem, type ShoppingSection } from "@bitebalance/contracts"

/**
 * Shop list clarity (2026-10-06): the Shop page groups rows under the eight supermarket sections the
 * catalog curates (`ShoppingListItem.section`), in the contract's display order, each group sorted
 * by name with a locale-aware comparison. Pure so the rule is testable without rendering; derived
 * from the served rows on every render — nothing here is keyed on row ids (they change on every save).
 *
 * THE DEGRADED CASE (founder, 2026-10-06): production before the next catalog load serves no
 * `section` at all, so every row maps to "Sonstiges". Then the list must read EXACTLY as the flat
 * list did before sections existed — no headers, the server's order, no name sort — because
 * sorting would reshuffle users' lists before sections exist. The condition is defined on the
 * rows actually shown in the grouped area: if the only section present is "other" (a missing
 * `section`, an explicit "other", or manual rows) there are no headers; as soon as one row has a
 * real section, headers show for EVERY non-empty group, including "other".
 *
 * KEEP IN SYNC with mobile/src/lib/shopSections.ts (this web twin adds the ticked-rows-sink rule).
 */
export interface ShopSectionGroup {
  section: ShoppingSection
  rows: ShoppingListItem[]
}

export interface GroupedShopRows {
  /** false in the degraded case (nothing but "other"): render one flat list without a header. */
  showHeaders: boolean
  groups: ShopSectionGroup[]
}

const KNOWN_SECTIONS: readonly string[] = SHOPPING_SECTIONS

/**
 * A row's section. Manual rows, rows the catalog has not curated yet — and rows whose `section` is a value this client does not know
 * (a newer server; the contract parse drops it, and this guard covers data that bypasses the parse) — are "other": a row is never dropped from the screen or
 * the export because its section is unfamiliar, and a list whose only sections are unknown is the flat list.
 */
export function sectionOf(item: ShoppingListItem): ShoppingSection {
  if (item.manual) return "other"
  const section: unknown = item.section
  return typeof section === "string" && KNOWN_SECTIONS.includes(section) ? (section as ShoppingSection) : "other"
}

/** True when at least one of the rows sits in a real (non-"other") section. */
export function hasRealSection(rows: readonly ShoppingListItem[]): boolean {
  return rows.some(row => sectionOf(row) !== "other")
}

/** Ticked rows sink to the bottom of their group (a stable sort: every other order is kept). Today's web rule, now per section. */
const sinkChecked = (rows: readonly ShoppingListItem[]): ShoppingListItem[] => [...rows].sort((a, b) => Number(a.checked) - Number(b.checked))

const byName = (locale: string) => (a: ShoppingListItem, b: ShoppingListItem) =>
  a.displayName.localeCompare(b.displayName, locale, { sensitivity: "base", numeric: true })

/**
 * Groups `rows` by section. Degraded case → one group in the INPUT order and `showHeaders: false`;
 * otherwise the non-empty sections in display order, rows sorted by name (a stable sort: equal names keep their input order).
 * In both cases ticked rows then sink to the bottom of their group, as the flat web list always did.
 */
export function groupBySection(rows: readonly ShoppingListItem[], locale: string): GroupedShopRows {
  if (rows.length === 0) return { showHeaders: false, groups: [] }
  if (!hasRealSection(rows)) return { showHeaders: false, groups: [{ section: "other", rows: sinkChecked(rows) }] }
  const groups = SHOPPING_SECTIONS.map(section => ({
    section,
    rows: sinkChecked(rows.filter(row => sectionOf(row) === section).sort(byName(locale))),
  })).filter(group => group.rows.length > 0)
  return { showHeaders: true, groups }
}
